import React from 'react';
import styles from '../AbasDocuments.module.scss';
import clsx from 'clsx';
import Chevron from 'assets/icons/chevron.svg?react';
import ArrowRight from 'assets/icons/arrow-long-right.svg?react';
import animationData from '../../../../assets/animations/loader_small.json';
import { LottieContainer } from '../../../../components/animations/LottieContainer.tsx';
import { ActionText } from '../../../../components/uikit/ActionText.tsx';
import { Button } from '../../../../components/uikit/buttons/Button.tsx';
import { downloadOrOpenBlobFile } from '../../../../helpers/download-helper.ts';
import { useProcessLinks } from '../../../../helpers/useProcessLinks.ts';
import { Tooltip } from '../../../../components/uikit/Tooltip.tsx';
import { Tip } from '../../../../components/uikit/Tip.tsx';
import { Trans, useTranslation } from 'react-i18next';
import { useAnalytics } from 'helpers/analytics.ts';
import { AbasDocumentsCategory, AbasDocumentGroup } from '../AbasDocuments.tsx';

const mail = 'supportHC@loewensteinmedical.com';

type OwnProps = {
  fileGroups: AbasDocumentGroup[];
  closedGroups: Set<string>;
  handleClickAccordion: (groupKey: string) => void;
  openAllAccordions: () => void;
  closeAllAccordions: () => void;
};

export const Accordion = (props: OwnProps) => {
  const {
    fileGroups,
    closedGroups,
    handleClickAccordion,
    openAllAccordions,
    closeAllAccordions,
  } = props;

  const i18n = useTranslation();
  const i18nPage = useTranslation('translation', {
    keyPrefix: 'Page.AbasDocuments',
  });
  const { trackEvent } = useAnalytics();

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    isTooltipVisible,
    closeTooltip,
  } = useProcessLinks();

  const isAllAccordionClosed =
    !!fileGroups.length && closedGroups.size === fileGroups.length;

  return (
    <div className={styles.marginBottom65}>
      {!!fileGroups.length && (
        <div className={styles.expandAll}>
          <Chevron
            data-test-id={'expand-all'}
            className={clsx(
              styles.pointer,
              styles.marginTop5,
              isAllAccordionClosed
                ? styles.rotate180
                : styles.marginBottomMinus2,
            )}
            onClick={() => {
              if (isAllAccordionClosed) {
                openAllAccordions();
              } else {
                closeAllAccordions();
              }
            }}
          />
        </div>
      )}
      {fileGroups.map((group, groupIndex) => {
        const groupKey = `${group.infosystemTypeName}.${groupIndex}`;
        const isClosed = closedGroups.has(groupKey);

        return (
          <div key={groupKey}>
            <div
              className={clsx(
                styles.rowContainer,
                styles.accordion,
                isClosed &&
                  groupIndex !== fileGroups.length - 1 &&
                  styles.borderBottom,
              )}
            >
              <Chevron
                data-test-id={'expand-one'}
                className={clsx(
                  styles.pointer,
                  isClosed ? styles.rotate180 : styles.marginBottomMinus2,
                )}
                onClick={() => handleClickAccordion(groupKey)}
              />
              <div className={clsx(styles.text, styles.groupName)}>
                {group.infosystemTypeName}
              </div>
            </div>
            {!isClosed && (
              <div>
                {group.rows.map((item, index) => {
                  const filePath = item.fileLink;
                  const fileName = item.fileName;
                  const fileExists = item.existsOnSharePoint;
                  const extension = filePath.includes('.')
                    ? filePath.slice(filePath.lastIndexOf('.') + 1)
                    : '';

                  const downloadKey = `download-${item.id}`;
                  const openKey = `open-${item.id}`;
                  const isDownloadLoading = processingLinks.has(downloadKey);
                  const isOpenLoading = processingLinks.has(openKey);
                  const hasLink = fileExists;

                  return (
                    <div
                      data-test-id={'item-container'}
                      key={`${item.id}.${index}`}
                      className={clsx(
                        styles.rowContainer,
                        styles.text,
                        styles.rowAccordion,
                      )}
                    >
                      <div className={clsx(styles.cell, styles.colFileName)}>
                        {item.fileName}
                      </div>
                      <div
                        className={clsx(
                          styles.cell,
                          styles.colFileLink,
                          styles.overflowEllipsis,
                        )}
                        title={item.fileLink}
                      >
                        {item.fileLink}
                      </div>
                      <div
                        className={clsx(styles.cell, styles.colActionDownload)}
                      >
                        {isDownloadLoading ? (
                          <LottieContainer
                            animationData={animationData}
                            segments={isDownloadLoading ? [1, 180] : [180, 270]}
                            loop={isDownloadLoading}
                            width={28}
                            height={28}
                          />
                        ) : !hasLink ? (
                          <Tip
                            tooltipText={i18n.t('link_unavailable_msg')}
                            textBody={
                              <div className={styles.disabledButton}>
                                {i18nPage.t('download')}
                              </div>
                            }
                            className={styles.tip}
                            tooltipClassName={styles.margins}
                          />
                        ) : (
                          <div>
                            <Button
                              testId={'download-button'}
                              className={clsx(styles.actionButton)}
                              title={i18nPage.t('download')}
                              onClick={() => {
                                trackEvent({
                                  category: AbasDocumentsCategory,
                                  action: 'Download file',
                                  name: downloadKey,
                                });
                                addProcessingLink(downloadKey);
                                downloadOrOpenBlobFile(filePath, fileName)
                                  .catch(showTooltip)
                                  .finally(() =>
                                    removeProcessingLink(downloadKey),
                                  );
                              }}
                            />
                            <div className={styles.caption}>{extension}</div>
                          </div>
                        )}
                      </div>
                      <div className={clsx(styles.cell, styles.colActionOpen)}>
                        {isOpenLoading ? (
                          <LottieContainer
                            animationData={animationData}
                            segments={isOpenLoading ? [1, 180] : [180, 270]}
                            loop={isOpenLoading}
                            width={28}
                            height={28}
                          />
                        ) : !hasLink ? (
                          <Tip
                            tooltipText={i18n.t('link_unavailable_msg')}
                            textBody={
                              <div className={styles.disabledButton}>
                                {i18nPage.t('open')}
                              </div>
                            }
                            className={styles.tip}
                            tooltipClassName={styles.margins}
                          />
                        ) : (
                          <div
                            data-test-id={'open-button'}
                            className={styles.openButton}
                            onClick={() => {
                              trackEvent({
                                category: AbasDocumentsCategory,
                                action: 'Open file',
                                name: openKey,
                              });
                              addProcessingLink(openKey);
                              downloadOrOpenBlobFile(filePath, '', true)
                                .catch(showTooltip)
                                .finally(() => removeProcessingLink(openKey));
                            }}
                          >
                            <ActionText text={i18nPage.t('open')} />
                            <ArrowRight />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {isTooltipVisible && (
        <Tooltip
          isError={true}
          closeTooltip={closeTooltip}
          bodyContent={
            <Trans
              i18nKey={'fileNotFound'}
              components={{
                supportLink: <a href={`mailto:${mail}`} />,
              }}
            />
          }
          className={styles.tooltip}
          showCancelButton={true}
          iconClassName={styles.tooltipIcon}
        />
      )}
    </div>
  );
};
